import { useEffect, useRef, useState, type JSX } from 'react';
import { useShallow } from 'zustand/react/shallow';
import type { Id } from '../../../core/model';
import { Slider } from '../../components/Slider';
import { useActions, useProject, useRuntime, useStudio } from '../../context';
import { safely } from '../../safe';
import '../panels.css';

/** T-bar: scrubs the transition towards preview; releasing at the end commits, anywhere else cancels. */
function TBar(): JSX.Element {
  const studio = useStudio();
  const [value, setValue] = useState(0);
  // The kernel commits (and swaps preview with program) the moment progress reaches 1. Anything sent
  // after that in the same drag would start a second transition straight back, so the bar goes quiet
  // until it is released.
  const committed = useRef(false);
  const dragging = useRef(false);

  const change = (next: number): void => {
    if (committed.current) return;
    committed.current = next >= 1;
    setValue(next);
    void safely(studio, () => studio.setTBar(next));
  };

  // A pointer drag ends with a release; a keyboard commit has none, so the bar resets by itself.
  useEffect(() => {
    if (value < 1) return;
    const handle = setTimeout(() => {
      if (!dragging.current) {
        committed.current = false;
        setValue(0);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [value]);

  const release = (): void => {
    dragging.current = false;
    if (!committed.current) void safely(studio, () => studio.setTBar(null));
    committed.current = false;
    setValue(0);
  };

  return (
    // From the keyboard the steps accumulate like a drag: End or enough arrow presses commit, and
    // Escape or leaving the control abandons the scrub.
    <div
      className="tbar"
      onPointerDown={() => void (dragging.current = true)}
      onKeyDown={(event) => {
        if (event.key !== 'Escape' || value === 0) return;
        event.stopPropagation();
        release();
      }}
      onBlur={() => {
        if (value !== 0 || committed.current) release();
      }}
    >
      <span className="ui-dim">T-bar</span>
      <Slider
        label="Transition T-bar"
        value={value}
        min={0}
        max={1}
        step={0.01}
        bigStep={0.1}
        commitOnKey={false}
        valueText={`${Math.round(value * 100)}%`}
        onChange={change}
        onCommit={release}
      />
    </div>
  );
}

/** Studio-mode transition controls: Transition, quick cut / fade, and the T-bar. */
export function TransitionBar(): JSX.Element {
  const studio = useStudio();
  const actions = useActions();
  const active = useRuntime((r) => r.transition.active);
  const quick = useProject(
    useShallow((p) => {
      const byKind = (kind: string): Id | null => p.transitionOrder.find((id) => p.transitions[id]?.kind === kind) ?? null;
      return { cut: byKind('cut'), fade: byKind('fade') };
    }),
  );

  /** Takes preview to program with a specific transition type, leaving the selected one untouched. */
  const quickTransition = (transitionId: Id): void =>
    void safely(studio, () => studio.transition(undefined, { transitionId }), 'Transition failed');

  return (
    <>
      <button type="button" className="ui-btn primary block" disabled={active} onClick={() => actions.transition()}>
        Transition
      </button>
      <div className="ui-row">
        <button type="button" className="ui-btn ui-grow" disabled={active || !quick.cut} onClick={() => quick.cut && quickTransition(quick.cut)}>
          Cut
        </button>
        <button type="button" className="ui-btn ui-grow" disabled={active || !quick.fade} onClick={() => quick.fade && quickTransition(quick.fade)}>
          Fade
        </button>
      </div>
      <TBar />
    </>
  );
}
